import React, { useCallback, useMemo, useState } from "react";
import classNames from "classnames";
import { EMPTY_STATE } from "@common/constants";
import { PlaidConnection } from "@common/types/plaidConnection";
import { IntegrationAccount } from "@common/types/integrationAccount";
import { Entity } from "@common/types/entity";
import { FinancialAccount } from "@common/types/financialAccount";
import { useFinancialAccountEntitySelection } from "@hooks/useFinancialAccountEntitySelection";
import { AccountCard } from "@components/financialAccount/AccountCard/AccountCard";
import { useChangeFinancialAccountEntityMutation } from "@mutations/financialAccount";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { UpdateFinancialAccountEntity } from "../UpdateFinancialAccountEntity";
import { FinancialAccountTypeSelect } from "../FinancialAccountTypeSelect";
import { isValidAccount } from "./helpers";
import { useAccountsConnectionContext } from "./AccountsConnectionContext";
import styles from "./AccountsConnectionWidget.module.scss";

interface AccountConnectionRowProps {
    integrationAccount: IntegrationAccount;
    financialAccount: FinancialAccount;
    connection?: PlaidConnection;
    onChangeAccountType?(isBusiness: boolean): Promise<void>;
    defaultEntity?: Entity;
}

export const AccountConnectionRow: React.FC<AccountConnectionRowProps> = ({
    integrationAccount,
    financialAccount,
    connection: connectionProp,
    onChangeAccountType,
    defaultEntity,
}) => {
    const connection =
        connectionProp ?? (integrationAccount.connection as PlaidConnection);
    const changeEntity =
        useChangeFinancialAccountEntityMutation(financialAccount);
    const { error } = useToaster();
    const {
        showBusinessEntitySelection,
        showPersonalEntitySelection,
        businessEntities,
        personalEntities,
        nonMockBusinessEntities,
    } = useFinancialAccountEntitySelection();
    const entitySelection =
        (showBusinessEntitySelection && !defaultEntity?.isBusiness) ||
        showPersonalEntitySelection;
    const { showAccountTypeWarning } = useAccountsConnectionContext();
    const [temporaryIsBusiness, setTemporaryIsBusiness] = useState<
        boolean | null
    >(null);

    const isBusinessSelection =
        temporaryIsBusiness ?? financialAccount.isBusiness;
    const showWarning =
        showAccountTypeWarning && !isValidAccount(integrationAccount);

    const onClassificationChanged = useCallback(
        (isBusiness: boolean) => {
            const availableEntities = isBusiness
                ? nonMockBusinessEntities
                : personalEntities;

            if (availableEntities.length === 1) {
                changeEntity.mutate(availableEntities[0], {
                    onError: () => error("Failed to change account company"),
                });
                setTemporaryIsBusiness(null);
            } else {
                setTemporaryIsBusiness(isBusiness);
            }

            onChangeAccountType?.(isBusiness);
        },
        [
            onChangeAccountType,
            changeEntity,
            error,
            nonMockBusinessEntities,
            personalEntities,
        ],
    );

    const selectedEntity = useMemo(() => {
        if (isBusinessSelection !== financialAccount.isBusiness) {
            return undefined;
        } else {
            return financialAccount.entity;
        }
    }, [isBusinessSelection, financialAccount]);

    let content: React.ReactNode;
    const availableEntities = isBusinessSelection
        ? businessEntities
        : personalEntities;

    if (isBusinessSelection === null || availableEntities.length === 1) {
        content = <div>{EMPTY_STATE}</div>;
    } else if (availableEntities.length === 0) {
        content = <div>No entities to administer</div>;
    } else {
        content = (
            <UpdateFinancialAccountEntity
                account={financialAccount}
                connection={connection}
                selectedEntity={selectedEntity ?? undefined}
                onUpdated={() => setTemporaryIsBusiness(null)}
                allowMockEntity
                isBusiness={isBusinessSelection ?? undefined}
            />
        );
    }

    return (
        <tr
            className={classNames({
                [styles.rowWarning]: showWarning,
            })}
            key={financialAccount.id}
        >
            <td className={styles.account}>
                <AccountCard
                    account={financialAccount}
                    connection={connection}
                    showNumber
                />
            </td>

            <td className={styles.classification}>
                <div className={styles.classificationContent}>
                    {showWarning && (
                        <span className="mr-3 text-warning">
                            Select{" "}
                            {isBusinessSelection !== null ? "Entity" : "Type"}
                        </span>
                    )}
                    <FinancialAccountTypeSelect
                        value={isBusinessSelection}
                        onChange={onClassificationChanged}
                        loading={changeEntity.isPending}
                        size="lg"
                    />
                </div>
            </td>

            {entitySelection && <td className={styles.entity}>{content}</td>}
        </tr>
    );
};
